import { isDesktop, showToast } from '@microfrontends/react-components/components/Utils'
import { RefObject } from 'react'
import viTrans from '../resources/translations/vi.json'
import enTrans from './../resources/translations/en.json'
import { DEFAULT_ALLOW_EXT } from '../components/Global/MainLayout/SideNavigation/constant'
import { TResumeSettingItem } from '../store'
import { formatDateFromGMT } from './date'
import { ELanguage } from '@microfrontends/react-components'

export function assetUrl(path) {
    return process.env.NEXT_PUBLIC_APP_ASSET_URL + path
}

export function imageServerUrl(path) {
    return process.env.NEXT_PUBLIC_IMAGE_URL + path
}

export function translate(key: string, langCode: ELanguage) {
    if (langCode === ELanguage.EN) {
        return enTrans[key] || key
    }

    if (!viTrans[key]) console.log(`${langCode} | --- ${key} --- translation is missing`)
    return viTrans[key] || key
}

export function createTFunction(langCode: ELanguage) {
    return (key: string) => {
        return translate(key, langCode)
    }
}

export function createReverseTranslateFunction(langCode: ELanguage) {
    return (key: string) => {
        return langCode === ELanguage.VI ? key : viTrans[key]
    }
}

export function removeScriptTag(s: string) {
    if (typeof document === 'undefined') {
        return s
    }

    const div = document.createElement('div')
    div.innerHTML = s

    const scripts = div.getElementsByTagName('script')

    let i = scripts.length
    while (i--) {
        scripts[i].parentNode?.removeChild(scripts[i])
    }

    return div.innerHTML
}

export function handleSuccessStatus(langCode: ELanguage, text: string) {
    showToast(langCode === ELanguage.EN ? 'Success' : 'Thành Công', text)
}

export function handleFailedStatus(langCode: ELanguage, text: string) {
    showToast(langCode === ELanguage.EN ? 'Error' : 'Lỗi', text, 'error')
}

export function scrollToRef(ref: RefObject<HTMLElement>) {
    if (typeof window === 'undefined' || typeof document === 'undefined' || !ref) {
        return
    }

    const elOffsetTop = ref.current?.getBoundingClientRect().top || 0
    const windowOffsetTop = document.documentElement.scrollTop
    window.scrollTo({
        top: elOffsetTop + windowOffsetTop - 90, // 90 = 74 (fixed header height) + 16 (padding)
        behavior: 'smooth'
    })
}

export const scrollContainerTo = (containerSelector: string, delay: number = 0, top: number = 0, left: number = 0) => {
    if (!isDesktop()) {
        setTimeout(() => {
            const scrollContainer = document.querySelector(containerSelector)
            scrollContainer?.scroll({ top, left })
        }, delay)
    }
}

export const handleFocusOnFirstErrorField = (container: string | HTMLElement) => {
    if (typeof document !== 'undefined' && container) {
        const inputErrorSelector: string = `[class*="input-module_inputError"]`
        const benefitErrorSelector: string = `.benefit-has-error .tag-item:first-child`

        // Set timeout to make sure that all states are set and re-rendering have been made
        setTimeout(() => {
            const containerElement = typeof container === 'string' ? document.querySelector(container) : container
            const firstErrorField = containerElement?.querySelectorAll(
                `${inputErrorSelector}, ${benefitErrorSelector}`
            )[0] as HTMLInputElement | null

            if (firstErrorField) {
                firstErrorField.focus()
            }
        }, 150)
    }
}

export function removeRedundantSpaces(str: string) {
    return str.replace(/\s+/g, ' ')
}

export function buildCompanyUrl(companyId: number, companySlug: string, langCode: ELanguage) {
    const prefixPath = langCode === ELanguage.EN ? 'companies' : 'nha-tuyen-dung'
    return `/${prefixPath}/${companySlug}-c${companyId}`
}

export function byteToSize(size: number) {
    let calSizeResume = ''

    const ONE_MB = 1024 * 1000
    const ONE_KB = 1024
    if (size >= ONE_MB) {
        calSizeResume = (size / ONE_MB).toFixed(1) + 'MB'
    } else {
        calSizeResume = (size / ONE_KB).toFixed(1) + 'KB'
    }
    return calSizeResume
}

export function commaSeparateNumber(val) {
    while (/(\d+)(\d{3})/.test(val.toString())) {
        val = val.toString().replace(/(\d+)(\d{3})/, '$1' + ',' + '$2')
    }
    return val
}

export function formatDate(date, join = '/') {
    date = date || new Date()

    const options = { day: '2-digit', month: '2-digit', year: 'numeric' }
    const formattedDate = date.toLocaleDateString('vi-VN', options).replaceAll('/', join)

    return formattedDate
}

export function transformToLatin(text: string) {
    text = text.toString().toLowerCase().trim()

    const sets = [
        { to: 'a', from: '[ÀÁÂÃÄÅÆĀĂĄẠẢẤẦẨẪẬẮẰẲẴẶἀ]' },
        { to: 'c', from: '[ÇĆĈČ]' },
        { to: 'd', from: '[ÐĎĐÞ]' },
        { to: 'e', from: '[ÈÉÊËĒĔĖĘĚẸẺẼẾỀỂỄỆ]' },
        { to: 'g', from: '[ĜĞĢǴ]' },
        { to: 'h', from: '[ĤḦ]' },
        { to: 'i', from: '[ÌÍÎÏĨĪĮİỈỊ]' },
        { to: 'j', from: '[Ĵ]' },
        { to: 'ij', from: '[Ĳ]' },
        { to: 'k', from: '[Ķ]' },
        { to: 'l', from: '[ĹĻĽŁ]' },
        { to: 'm', from: '[Ḿ]' },
        { to: 'n', from: '[ÑŃŅŇ]' },
        { to: 'o', from: '[ÒÓÔÕÖØŌŎŐỌỎỐỒỔỖỘỚỜỞỠỢǪǬƠ]' },
        { to: 'oe', from: '[Œ]' },
        { to: 'p', from: '[ṕ]' },
        { to: 'r', from: '[ŔŖŘ]' },
        { to: 's', from: '[ßŚŜŞŠȘ]' },
        { to: 't', from: '[ŢŤ]' },
        { to: 'u', from: '[ÙÚÛÜŨŪŬŮŰŲỤỦỨỪỬỮỰƯ]' },
        { to: 'w', from: '[ẂŴẀẄ]' },
        { to: 'x', from: '[ẍ]' },
        { to: 'y', from: '[ÝŶŸỲỴỶỸ]' },
        { to: 'z', from: '[ŹŻŽ]' },
        { to: '-', from: "[·/_,:;']" }
    ]

    sets.forEach((set) => {
        text = text.replace(new RegExp(set.from, 'gi'), set.to)
    })

    return text
}

export function slugify(slug: string) {
    slug = slug.replace(/^\s+|\s+$/g, '') // trim
    slug = slug.toLowerCase()

    // remove accents
    slug = transformToLatin(slug)

    slug = slug
        .replace(/[^a-z0-9 -]/g, '-') // invalid chars to -
        .replace(/\s+/g, '-') // collapse whitespace and replace by -
        .replace(/-+/g, '-') // collapse dashes

    return slug
}

export function extractFilenameInfo(fileName: string) {
    const splitFileNameByDot = fileName.split('.')
    if (!fileName.length || splitFileNameByDot.length < 2) {
        return {
            name: '',
            extension: ''
        }
    }
    const extension = splitFileNameByDot.pop()!.toLowerCase()
    return {
        name: splitFileNameByDot.join(''),
        extension: DEFAULT_ALLOW_EXT.some((extensionWithDot) => extensionWithDot.indexOf(extension) === 1)
            ? extension
            : ''
    }
}

export const formatAttachmentList = (data: TResumeSettingItem[] | []) => {
    if (!data.length) {
        return []
    }
    return (data || []).map((item: TResumeSettingItem) => {
        const { name, extension } = extractFilenameInfo(item?.fileName)
        return {
            ...item,
            id: item?.id,
            link: item?.link,
            updatedDate: formatDateFromGMT(item?.updatedDate),
            name,
            extension
        }
    })
}

export function stripFileName(fileName: string, maxLength: number = 15) {
    let name = ''
    let matchExt = ''

    if (fileName.length <= maxLength) {
        return fileName
    }
    // Find extension in filename
    DEFAULT_ALLOW_EXT.map((extension) => {
        if (fileName.indexOf(extension) > 0) {
            // Match
            matchExt = extension
        }
    })
    name = fileName.replace(matchExt, '').slice(0, maxLength)
    return `${name}..${matchExt}`
}
